import React, { Component } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import { CardPageLayout } from "component-lib/CardPageLayout";
import { PrimaryButton, ButtonSizeOptions, SecondaryButton, DEAL_STATUSES, PROPERTY_ASSET_TYPES, LOAN_SENIORITY_TYPES } from '@loanbossinc/component-library';
import { mapCompanySelector, mapGuestPermissions } from "store/state/auth";
import { dealGet, getWinningQuotes } from './dealSettings.store';
import { getLoansByPreferenceAndLoanSeniority } from "store/state/loan/loan";
import heroCardMetrics from "./heroCardMetrics";
import { GenericHeroCard } from "component-lib/";
import { DealGeneralInfo } from "./deal-general-information/dealGeneralInfo";
import { DealOrganization } from "./deal-organization/dealOrganization";
import DealIntegrations from "./deal-integrations/dealIntegrations";
import { IsProd } from 'store/utils/helpers';

import { DealDisposition } from "./deal-disposition/dealDisposition";

const StyledModuleContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const StyledBodyWrapper = styled.div`
  && {
    display: flex;
    flex-direction: column;
  }
`;
const StyledTitleSection = styled.div`
  && {
    display: flex;
    flex-direction: row;

    .title {
      font-family: Work Sans;
      font-style: normal;
      font-weight: 500;
      font-size: 24px;
      line-height: 28px;
      display: flex;
      align-items: center;
      text-align: center;

      color: #000000;
    }
  }
`;

class DealSettings extends Component {
  state = {
    isAllExpanded: false,
    isAllCollapsed: false,
    heroCardDealMetrics: [],
    openModal: { name: null, data: null },
    dealHeroCardButtons: []
  };

  componentDidMount() {
    const { dealId } = this.props.match.params;
    this.props.dealGet({ dealId });

    const curriedSeniorQuery = this.getLoansBySeniority(this.getComparableLoanColumns(), "comparable_senior_loans", LOAN_SENIORITY_TYPES.SENIOR);
    curriedSeniorQuery(0, 100);
    const curriedMezzQuery = this.getLoansBySeniority(this.getComparableLoanColumns(), "comparable_mezz_loans", LOAN_SENIORITY_TYPES.SUBORDINATE);
    curriedMezzQuery(0, 100);
    const curriedSupplementalQuery = this.getLoansBySeniority(
      this.getComparableLoanColumns(),
      "comparable_supplemental_loans",
      LOAN_SENIORITY_TYPES.SUPPLEMENTAL
    );
    curriedSupplementalQuery(0, 100);

    this.getPaginatedSenior(0, 10, "ASC");
    this.getPaginatedMezz(0, 10, "ASC");
    this.getPaginatedSubsidy(0, 10, "ASC");
    this.getPaginatedSupplemental(0, 10, "ASC");
    this.setState({ winningQuotesRequested: false });
  }

  componentDidUpdate(prevProps) {
    if (this.props !== prevProps) {
      this.findMetrics();
      this.setHeroCardButtons();
    }
  }

  getComparableLoanColumns = () => {
    return ["amount", "loanTermMonths", "rateTypeId"];
  };

  getPaginatedSenior = (page, pageSize, sortOrder) => {
    const curriedQuery = this.getLoansBySeniority(null, "senior_loan_table", LOAN_SENIORITY_TYPES.SENIOR);
    curriedQuery(page, pageSize, sortOrder);
  };

  getPaginatedMezz = (page, pageSize, sortOrder) => {
    const curriedQuery = this.getLoansBySeniority(null, "mezz_loan_table", LOAN_SENIORITY_TYPES.SUBORDINATE);
    curriedQuery(page, pageSize, sortOrder);
  };

  getPaginatedSubsidy = (page, pageSize, sortOrder) => {
    const curriedQuery = this.getLoansBySeniority(null, "subsidy_loan_table", LOAN_SENIORITY_TYPES.SUBSIDY);
    curriedQuery(page, pageSize, sortOrder);
  };

  getPaginatedSupplemental = (page, pageSize, sortOrder) => {
    const curriedQuery = this.getLoansBySeniority(null, "supplemental_loan_table", LOAN_SENIORITY_TYPES.SUPPLEMENTAL);
    curriedQuery(page, pageSize, sortOrder);
  };

  getLoansBySeniority = (desiredColumns, subKey, loanSeniorityId) => {
    const { dealId } = this.props.match.params;
    return (page, pageSize, sortOrder) => {
      this.props.getLoansByPreferenceAndLoanSeniority({
        subKey,
        dealId,
        loanSeniorityId,
        columns: desiredColumns,
        page,
        pageSize,
        sortOrder,
        includePassedRejected: this.props.authentication.isGuest && this.props.guestPermissions && !this.props.guestPermissions.pipelineAllowQuoteStatus
      });
    };
  };

  setHeroCardButtons() {
    const { deal } = this.props;
    if (!deal || !deal.dealStatusId) return null;
    let buttons = [];
    this.setState({ dealHeroCardButtons: buttons });
  }

  findMetrics = () => {
    const { deal } = this.props;
    if (!deal || !deal.dealMetrics) return;
    const dealStatusId = deal.dealStatusId;
    const projectTypeId = deal.projectTypeId;
    const isMultiFamily = deal.properties.some(p => p.assetTypeId === PROPERTY_ASSET_TYPES.MULTIFAMILY);

    const heroCardDealMetrics = heroCardMetrics.reduce((accum, heroMetric) => {
      const metric = deal.dealMetrics.find(metric => metric.metricId === heroMetric.metricId);
      const shouldInclude =
        heroMetric.activeDealStatus.includes(dealStatusId) &&
        (heroMetric.activeProjectType.length === 0 || heroMetric.activeProjectType.includes(projectTypeId)) &&
        (heroMetric.isMFOnly !== null || (heroMetric.isMFOnly && isMultiFamily));

      if (metric && shouldInclude) accum.push({ ...heroMetric, ...metric });
      else if (shouldInclude) accum.push({ ...heroMetric });

      return accum;
    }, []);

    this.setState({ heroCardDealMetrics });
  };

  // render commands
  renderBody = () => {
    const { deal, history, company, authentication} = this.props;
    const { dealId } = this.props.match.params;
    const { isAllExpanded, isAllCollapsed } = this.state;
    deal.authentication = authentication;
    const groupProps = {
      data: deal,
      dealId,
      history,
      shouldExpandAll: isAllExpanded,
      onToggleExpandAll: () => this.setState({ isAllExpanded: false }),
      shouldCollapseAll: isAllCollapsed,
      onToggleCollapseAll: () => this.setState({ isAllCollapsed: false })
    };

    return (
      <StyledBodyWrapper>
        <StyledTitleSection>
          <div className="title">Deal Settings</div>
          <SecondaryButton
            style={{ marginLeft: "auto" }}
            label="Collapse All"
            onClick={() => this.setState({ isAllCollapsed: true })}
            size={ButtonSizeOptions.smFluid}
          />
          <PrimaryButton
            style={{ marginLeft: 24 }}
            label="Expand All"
            onClick={() => this.setState({ isAllExpanded: true })}
            size={ButtonSizeOptions.smFluid}
          />
        </StyledTitleSection>

        <DealGeneralInfo {...groupProps} />
        <DealIntegrations {...groupProps} company={company} />
        <DealOrganization {...groupProps} />
        <DealDisposition {...groupProps} />
      </StyledBodyWrapper>
    );
  };

  renderHeroCard = () => {
    const { deal } = this.props;
    if (!deal) return null;

    return (
      <div>
        <GenericHeroCard {...{ ...this.props, buttons: this.state.dealHeroCardButtons, deal: deal, title: deal.name, backLabel: "Deal Summary" }} />
      </div>
    );
  };

  render() {
    const { deal, isLoading } = this.props;
    if (!deal) return null;

    return (
      <>
        <CardPageLayout
          {...this.props}
          isReversed
          bannerImageUrl={this.props.deal && this.props.deal.imageLarge}
          cardComponent={this.renderHeroCard()}
          bodyComponent={<StyledBodyWrapper>{this.renderBody()}</StyledBodyWrapper>}
        />
      </>
    );
  }
}

const mapStateToProps = ({ dealSettings, authentication, loading, loan, }) => ({
  ...dealSettings
  , guestPermission: mapGuestPermissions(authentication, dealSettings.guestPermission)
  , company: mapCompanySelector(authentication)
  , authentication
  , isLoading: loading.deal_file_upload

  , seniorLoanData: loan.loansByPreference.senior_loan_table || {}
  , mezzLoanData: loan.loansByPreference.mezz_loan_table || {}
	, subsidyLoanData: loan.loansByPreference.subsidy_loan_table || {}
  , supplementalLoanData: loan.loansByPreference.supplemental_loan_table || {}

  , comparableSeniorLoans: loan.loansByPreference.comparable_senior_loans || {}
  , comparableMezzLoans: loan.loansByPreference.comparable_mezz_loans || {}
  , comparableSupplementalLoans: loan.loansByPreference.comparable_supplemental_loans || {}
});

export default compose(
  withRouter
  , connect(mapStateToProps, {
    dealGet
    , getLoansByPreferenceAndLoanSeniority
    , getWinningQuotes
  })
)(DealSettings);
